/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils-define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace ns
*/
var ns = {};

/**
* @name isEvenInt32
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/int32-is-even}
*/
import isEvenInt32 from '@stdlib/math-base-assert-int32-is-even';
setReadOnly( ns, 'isEvenInt32', isEvenInt32 );

/**
* @name isOddInt32
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/int32-is-odd}
*/
import isOddInt32 from '@stdlib/math-base-assert-int32-is-odd';
setReadOnly( ns, 'isOddInt32', isOddInt32 );

/**
* @name isComposite
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-composite}
*/
import isComposite from '@stdlib/math-base-assert-is-composite';
setReadOnly( ns, 'isComposite', isComposite );

/**
* @name isCoprime
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-coprime}
*/
import isCoprime from '@stdlib/math-base-assert-is-coprime';
setReadOnly( ns, 'isCoprime', isCoprime );

/**
* @name isEven
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-even}
*/
import isEven from '@stdlib/math-base-assert-is-even';
setReadOnly( ns, 'isEven', isEven );

/**
* @name isEvenf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-evenf}
*/
import isEvenf from '@stdlib/math-base-assert-is-evenf';
setReadOnly( ns, 'isEvenf', isEvenf );

/**
* @name isFinite
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-finite}
*/
import isFinite from '@stdlib/math-base-assert-is-finite';
setReadOnly( ns, 'isFinite', isFinite );

/**
* @name isFinitef
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-finitef}
*/
import isFinitef from '@stdlib/math-base-assert-is-finitef';
setReadOnly( ns, 'isFinitef', isFinitef );

/**
* @name isInfinite
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-infinite}
*/
import isInfinite from '@stdlib/math-base-assert-is-infinite';
setReadOnly( ns, 'isInfinite', isInfinite );

/**
* @name isInfinitef
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-infinitef}
*/
import isInfinitef from '@stdlib/math-base-assert-is-infinitef';
setReadOnly( ns, 'isInfinitef', isInfinitef );

/**
* @name isInteger
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-integer}
*/
import isInteger from '@stdlib/math-base-assert-is-integer';
setReadOnly( ns, 'isInteger', isInteger );

/**
* @name isIntegerf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-integerf}
*/
import isIntegerf from '@stdlib/math-base-assert-is-integerf';
setReadOnly( ns, 'isIntegerf', isIntegerf );

/**
* @name isnan
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-nan}
*/
import isnan from '@stdlib/math-base-assert-is-nan';
setReadOnly( ns, 'isnan', isnan );

/**
* @name isnanf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-nanf}
*/
import isnanf from '@stdlib/math-base-assert-is-nanf';
setReadOnly( ns, 'isnanf', isnanf );

/**
* @name isNegativeFinite
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-negative-finite}
*/
import isNegativeFinite from '@stdlib/math-base-assert-is-negative-finite';
setReadOnly( ns, 'isNegativeFinite', isNegativeFinite );

/**
* @name isNegativeInteger
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-negative-integer}
*/
import isNegativeInteger from '@stdlib/math-base-assert-is-negative-integer';
setReadOnly( ns, 'isNegativeInteger', isNegativeInteger );

/**
* @name isNegativeIntegerf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-negative-integerf}
*/
import isNegativeIntegerf from '@stdlib/math-base-assert-is-negative-integerf';
setReadOnly( ns, 'isNegativeIntegerf', isNegativeIntegerf );

/**
* @name isNegativeZero
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-negative-zero}
*/
import isNegativeZero from '@stdlib/math-base-assert-is-negative-zero';
setReadOnly( ns, 'isNegativeZero', isNegativeZero );

/**
* @name isNegativeZerof
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-negative-zerof}
*/
import isNegativeZerof from '@stdlib/math-base-assert-is-negative-zerof';
setReadOnly( ns, 'isNegativeZerof', isNegativeZerof );

/**
* @name isNonNegativeFinite
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-nonnegative-finite}
*/
import isNonNegativeFinite from '@stdlib/math-base-assert-is-nonnegative-finite';
setReadOnly( ns, 'isNonNegativeFinite', isNonNegativeFinite );

/**
* @name isNonNegativeInteger
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-nonnegative-integer}
*/
import isNonNegativeInteger from '@stdlib/math-base-assert-is-nonnegative-integer';
setReadOnly( ns, 'isNonNegativeInteger', isNonNegativeInteger );

/**
* @name isNonnegativeIntegerf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-nonnegative-integerf}
*/
import isNonnegativeIntegerf from '@stdlib/math-base-assert-is-nonnegative-integerf';
setReadOnly( ns, 'isNonnegativeIntegerf', isNonnegativeIntegerf );

/**
* @name isNonPositiveFinite
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-nonpositive-finite}
*/
import isNonPositiveFinite from '@stdlib/math-base-assert-is-nonpositive-finite';
setReadOnly( ns, 'isNonPositiveFinite', isNonPositiveFinite );

/**
* @name isNonPositiveInteger
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-nonpositive-integer}
*/
import isNonPositiveInteger from '@stdlib/math-base-assert-is-nonpositive-integer';
setReadOnly( ns, 'isNonPositiveInteger', isNonPositiveInteger );

/**
* @name isOdd
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-odd}
*/
import isOdd from '@stdlib/math-base-assert-is-odd';
setReadOnly( ns, 'isOdd', isOdd );

/**
* @name isOddf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-oddf}
*/
import isOddf from '@stdlib/math-base-assert-is-oddf';
setReadOnly( ns, 'isOddf', isOddf );

/**
* @name isPositiveFinite
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-positive-finite}
*/
import isPositiveFinite from '@stdlib/math-base-assert-is-positive-finite';
setReadOnly( ns, 'isPositiveFinite', isPositiveFinite );

/**
* @name isPositiveInteger
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-positive-integer}
*/
import isPositiveInteger from '@stdlib/math-base-assert-is-positive-integer';
setReadOnly( ns, 'isPositiveInteger', isPositiveInteger );

/**
* @name isPositiveZero
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-positive-zero}
*/
import isPositiveZero from '@stdlib/math-base-assert-is-positive-zero';
setReadOnly( ns, 'isPositiveZero', isPositiveZero );

/**
* @name isPositiveZerof
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-positive-zerof}
*/
import isPositiveZerof from '@stdlib/math-base-assert-is-positive-zerof';
setReadOnly( ns, 'isPositiveZerof', isPositiveZerof );

/**
* @name isPrime
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-prime}
*/
import isPrime from '@stdlib/math-base-assert-is-prime';
setReadOnly( ns, 'isPrime', isPrime );

/**
* @name isProbability
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-probability}
*/
import isProbability from '@stdlib/math-base-assert-is-probability';
setReadOnly( ns, 'isProbability', isProbability );

/**
* @name isProbabilityf
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-probabilityf}
*/
import isProbabilityf from '@stdlib/math-base-assert-is-probabilityf';
setReadOnly( ns, 'isProbabilityf', isProbabilityf );

/**
* @name isSafeInteger
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/is-safe-integer}
*/
import isSafeInteger from '@stdlib/math-base-assert-is-safe-integer';
setReadOnly( ns, 'isSafeInteger', isSafeInteger );

/**
* @name isPow2Uint32
* @memberof ns
* @readonly
* @type {Function}
* @see {@link module:@stdlib/math/base/assert/uint32-is-pow2}
*/
import isPow2Uint32 from '@stdlib/math-base-assert-uint32-is-pow2';
setReadOnly( ns, 'isPow2Uint32', isPow2Uint32 );


// EXPORTS //

export default ns;
